import {AbstractControl, FormArray, FormGroup} from '@angular/forms';
import {Subscription} from 'rxjs';
import {DoButtonComponent} from '../do-button/do-button.component';

/**
 * Invalid fields under `control`. A control whose errors carry `invalidFields` (a sub-form
 * component -- basics, tracks, settings -- reporting the fields it holds) counts that many; any
 * other invalid leaf counts one; groups and arrays add up their children, plus one for an error of their own.
 */
export function countInvalidFields(control: AbstractControl): number {
  const reported = control.errors?.['invalidFields'];
  if (typeof reported === 'number') {
    // A sub-form invalid only through a non-field reason reports it as 'blocking' (counted 0, said in
    // words); otherwise an invalid control never counts 0.
    return typeof control.errors?.['blocking'] === 'string' ? reported : Math.max(reported, control.invalid ? 1 : 0);
  }
  if (control instanceof FormGroup || control instanceof FormArray) {
    // Its own error (e.g. a required, empty item list) is one more thing to fix besides its children.
    const own = control.errors ? 1 : 0;
    return Object.values(control.controls).reduce((n: number, c: AbstractControl) => n + countInvalidFields(c), own);
  }
  return control.invalid ? 1 : 0;
}

/**
 * The message a refused submit shows, shared by the PO forms ("1 field needs attention." /
 * "N fields need attention."). Shown with severity cl-alert-danger, which puts the icon in front.
 */
export function invalidFieldMessage(control: AbstractControl): string {
  const n = countInvalidFields(control);
  const parts = n === 0 ? [] : [n === 1 ? '1 field needs attention.' : `${n} fields need attention.`];
  return [...parts, ...blockingReasons(control)].join(' ') || 'The form cannot be submitted yet.';
}

/**
 * Reasons a control is invalid that are not a field (no input to mark or focus): a sub-form reports
 * them as `errors.blocking` (a sentence), e.g. a pre-release source on a format that cannot watermark.
 */
export function blockingReasons(control: AbstractControl): string[] {
  const own = typeof control.errors?.['blocking'] === 'string' ? [control.errors['blocking'] as string] : [];
  const children = control instanceof FormGroup || control instanceof FormArray
    ? Object.values(control.controls).flatMap((c: AbstractControl) => blockingReasons(c))
    : [];
  return [...new Set([...own, ...children])];
}

const followers = new WeakMap<DoButtonComponent, Subscription>();

/**
 * A refused submit: re-enables the button and shows the count of fields that need attention. The
 * message does not time out -- it follows the form, updating the count as fields change and
 * disappearing once the form is valid (a validation error stays until it is resolved).
 */
export function reportInvalid(button: DoButtonComponent, form: AbstractControl, focusWithin?: string | HTMLElement): void {
  if (focusWithin) {
    focusFirstInvalid(focusWithin);
  }
  followers.get(button)?.unsubscribe();
  button.done(invalidFieldMessage(form), {severity: 'cl-alert-danger', duration: -1});
  const sub = form.statusChanges.subscribe(() => {
    if (form.valid) {
      button.clearMessage();
      sub.unsubscribe();
      followers.delete(button);
    } else {
      button.info(invalidFieldMessage(form), {severity: 'cl-alert-danger', duration: -1});
    }
  });
  followers.set(button, sub);
}

/**
 * Focuses the first invalid field under `scope` (an element id or element), once the refused submit
 * has rendered. castlabs-ui's clFormFieldFocusFirstInvalid takes the first `.ng-invalid` in DOM order,
 * which is usually a sub-form host or a form container -- not focusable, so focus stayed on the
 * button -- and runs before change detection has set the gated `.invalid` classes.
 */
export function focusFirstInvalid(scope: string | HTMLElement): void {
  setTimeout(() => {
    const root = typeof scope === 'string' ? document.getElementById(scope) : scope;
    const invalid = Array.from(root?.querySelectorAll<HTMLElement>(
      ['input', 'select', 'textarea'].map(t => `${t}.invalid:not([disabled]), ${t}.ng-invalid:not([disabled]):not([type=hidden])`).join(', ')) ?? []);
    if (!invalid.length) return;
    // A field inside a collapsed card cannot be seen or take focus: open every one that holds an
    // invalid field.
    for (const f of invalid) {
      for (let d = f.closest('details:not([open])'); d; d = d.parentElement?.closest('details:not([open])') ?? null) {
        (d as HTMLDetailsElement).open = true;
      }
    }
    focusField(invalid[0]);
  });
}

function focusField(field: HTMLElement): void {
  field.focus({preventScroll: true});
  // Instant, not the page's `scroll-behavior: smooth` (bootstrap reboot): on the IFE page the smooth
  // scroll was cancelled and the window never moved (VTK-3570 recheck on ba6d757a). An instant scroll
  // is synchronous, so the window check below reads the settled position.
  // Nearest scrolling container first (e.g. a dialog body) ...
  field.scrollIntoView?.({block: 'nearest', behavior: 'instant'});
  // ... then the page itself, if an overflow-hidden ancestor took the scroll instead of the window.
  const top = field.getBoundingClientRect().top;
  if (top < 0 || top > window.innerHeight) {
    window.scrollTo({top: window.scrollY + top - window.innerHeight / 2, behavior: 'instant'});
  }
}

/** The PO forms' submit label: "Start encode", with the item count once there are items. */
export function startEncodeLabel(items: number): string {
  return items > 0 ? `Start encode (${items} ${items === 1 ? 'item' : 'items'})` : 'Start encode';
}
